/**
 * DealerDetail — Super-admin dealer detail page with tabs.
 *
 * D3 spec:
 * - Tabs: Overview · People (users) · Settings (admin only) · Audit Log · Reports
 * - People tab actions: Manage (edit info + send password-reset email — IDI never sees/sets passwords)
 *   · Deactivate · Remove · Paid↔Viewer switch (IDI-226: enabled Salesperson rows;
 *   the server refuses a Paid seat past the dealer's paid_seats with a 422)
 * - Overview: "Edit paid seats" writes the dealer's paid_seats (IDI-226)
 * - Seat management: Paid seats counted, Viewer seats free/unlimited
 *
 * Route: /super-admin/dealers/:id (Settings tab: /super-admin/dealers/:id/settings/:area, IDI-63)
 */

import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/20/solid';
import PageHeader from '../ui/PageHeader';
import { Button } from '../ui/Button';
import StatusPill, { humanizeEnum } from '../ui/StatusPill';
import useTableSort from '../ui/useTableSort';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';
import { SkeletonCard } from '../ui/Skeleton';
import SelectMenu from '../ui/SelectMenu';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../ui/Dialog';
import { superAdminAPI } from '../../services/api';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'people', label: 'People' },
  { id: 'settings', label: 'Settings' },
  { id: 'audit', label: 'Audit Log' },
  { id: 'reports', label: 'Reports' },
];

export default function DealerDetail() {
  const { id } = useParams();
  const [dealer, setDealer] = useState(null);
  // IDI-622 (D1): the dealer's own Users & Roles logins; null while loading.
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [tabState, setActiveTab] = useState('overview');
  // IDI-63: the Settings tab is the dealer's Settings workspace, addressed by
  // /super-admin/dealers/<id>/settings/<area> so a reload keeps the dealer.
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onSettings = /^\/super-admin\/dealers\/[^/]+\/settings(\/|$)/.test(pathname);
  const activeTab = onSettings ? 'settings' : tabState;
  const selectTab = (tabId) => {
    if (tabId === 'settings') {
      navigate(`/super-admin/dealers/${encodeURIComponent(id)}/settings/company`);
      return;
    }
    setActiveTab(tabId);
    if (onSettings) navigate(`/super-admin/dealers/${encodeURIComponent(id)}`);
  };

  useEffect(() => {
    async function loadDealer() {
      try {
        setLoadError(false);
        const res = await superAdminAPI.getDealer(id);
        setDealer(res);
      } catch {
        // IDI-65: a failed request is NOT the same as "contractor not found".
        // Distinguish so we don't tell the user a real dealer doesn't exist.
        setLoadError(true);
        setDealer(null);
      } finally {
        setLoading(false);
      }
    }
    loadDealer();
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setUsers(null);
    Promise.resolve()
      .then(() => superAdminAPI.getDealerUsers())
      .then((list) => { if (!cancelled) setUsers(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setUsers([]); });
    return () => { cancelled = true; };
  }, [id]);

  // IDI-623 (D7): re-read the list after a temp password is issued. The
  // current rows stay on screen while it loads, and a failed re-read keeps
  // them. Add User still appends without a re-read (IDI-252).
  const reloadUsers = () => {
    Promise.resolve()
      .then(() => superAdminAPI.getDealerUsers())
      .then((list) => { if (Array.isArray(list)) setUsers(list); })
      .catch(() => {});
  };

  if (loading) return <div className="px-6"><SkeletonCard /><SkeletonCard /></div>;

  if (loadError) {
    return (
      <div className="px-6 space-y-4">
        <PageHeader title="Couldn't Load Contractor" backHref="/super-admin/dealers" />
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          The request failed — this does not mean the contractor is missing. Try again.
        </p>
        <div>
          <Button variant="secondary" onClick={() => window.location.reload()}>Try again</Button>
        </div>
      </div>
    );
  }

  if (!dealer) {
    return (
      <div className="px-6 space-y-4">
        <PageHeader title="Contractor Not Found" backHref="/super-admin/dealers" />
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          This contractor could not be loaded.
        </p>
      </div>
    );
  }

  return (
    <div className="px-6 space-y-6">
      <PageHeader
        title={dealer.name}
        subtitle={`${dealer.city}, ${dealer.state}`}
        backHref="/super-admin/dealers"
        actions={<StatusPill status={dealer.status} />}
      />

      {/* Privacy banner — tenant boundary */}
      <div
        className="card p-3 flex items-center gap-2"
        style={{ borderColor: 'var(--amber-200)', backgroundColor: 'var(--amber-50)' }}
      >
        <LockClosedIcon className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--amber-700)' }} />
        <span className="text-sm" style={{ color: 'var(--amber-700)' }}>
          Settings and users only. Every change is recorded in the Audit Log. Customers, quotes, jobs and invoices stay private.
        </span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b" style={{ borderColor: 'var(--color-border)' }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => selectTab(tab.id)}
            className="px-4 py-2 text-sm font-medium transition-colors relative"
            style={{
              color: activeTab === tab.id ? 'var(--color-action-primary)' : 'var(--color-text-secondary)',
            }}
          >
            {tab.label}
            {activeTab === tab.id && (
              <span
                className="absolute bottom-0 left-0 right-0 h-0.5"
                style={{ backgroundColor: 'var(--color-action-primary)' }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <OverviewTab
          dealer={dealer}
          users={users}
          onPaidSeatsSaved={(n) => setDealer((d) => ({ ...d, paidSeats: n, seatsTotal: n }))}
        />
      )}
      {activeTab === 'people' && (
        <PeopleTab
          users={users}
          onUserAdded={(u) => setUsers((prev) => [...(prev || []), u])}
          onPasswordIssued={reloadUsers}
          onSeatChanged={(userId, seat) => setUsers((prev) => (prev || []).map((u) => (
            u.id === userId ? { ...u, seatType: seat, seat_type: seat } : u
          )))}
        />
      )}
      {onSettings && <Outlet />}
      {activeTab === 'audit' && <AuditLogTab dealerId={id} />}
      {activeTab === 'reports' && <ReportsTab dealer={dealer} />}
    </div>
  );
}

// ─── IDI-622 seat / login helpers (D2, D3) ──────────────────────────────────

// IDI-767 D3: a stored seat reads as stored; an unset seat counts as Paid,
// except an unset Field Tech, which reads Viewer (as the server's
// idi226HoldsPaid and Users & Roles' paidUsed count it).
function seatLabel(u) {
  const s = u.seatType || u.seat_type;
  if (s) return s === 'viewer' ? 'Viewer' : 'Paid';
  const role = (u.role || '').toLowerCase();
  return role === 'field_tech' || role === 'field_technician' ? 'Viewer' : 'Paid';
}
const isEnabled = (u) => u.status === 'active';
// IDI-226 (D7): only an enabled Salesperson switches; Admin stays Paid, Field Tech Viewer.
const canSwitchSeat = (u) => isEnabled(u) && (u.role || '').toLowerCase() === 'salesperson';
const isNoSeats = (err) => (err?.response?.status ?? err?.status) === 422;
const RAISE_PAID_SEATS_MSG = 'No paid seats available. Raise Paid seats on Overview first.';
const lastLoginOf = (u) => u.lastLogin || u.last_login || null;
function fmtDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

/** Overview figures from the dealer's logins (D2, D3). */
function summarizeDealerUsers(users) {
  const list = users || [];
  const times = (pick) => list.map(pick).filter(Boolean)
    .map((v) => ({ v, t: new Date(v).getTime() })).filter((x) => !Number.isNaN(x.t));
  const created = times((u) => u.created || u.createdAt).sort((a, b) => a.t - b.t)[0];
  const last = times(lastLoginOf).sort((a, b) => b.t - a.t)[0];
  return {
    created: created ? created.v : null,
    lastLogin: last ? last.v : null,
    paidInUse: list.filter((u) => isEnabled(u) && seatLabel(u) === 'Paid').length,
    viewers: list.filter((u) => isEnabled(u) && seatLabel(u) === 'Viewer').length,
  };
}

// ─── Overview Tab ────────────────────────────────────────────────────────────

function OverviewTab({ dealer, users, onPaidSeatsSaved }) {
  const sum = summarizeDealerUsers(users);
  const pending = users === null;
  const paidTotal = dealer.paidSeats ?? dealer.seatsTotal ?? 0;
  // IDI-226 (4): IDI sets the dealer's paid_seats here.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [seatError, setSeatError] = useState(null);
  const [savingSeats, setSavingSeats] = useState(false);
  const startEdit = () => { setDraft(String(paidTotal || '')); setSeatError(null); setEditing(true); };
  const savePaidSeats = async () => {
    const n = Number(draft);
    if (!Number.isInteger(n) || n < 1) {
      setSeatError('Paid seats must be at least 1.');
      return;
    }
    setSavingSeats(true);
    setSeatError(null);
    try {
      await superAdminAPI.updateDealer(dealer.id, { paid_seats: n });
      if (onPaidSeatsSaved) onPaidSeatsSaved(n);
      setEditing(false);
    } catch {
      setSeatError("Couldn't save paid seats. Try again.");
    } finally {
      setSavingSeats(false);
    }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="card p-4 space-y-3" style={{ borderColor: 'var(--color-border)' }}>
        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Account Info</h3>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt style={{ color: 'var(--color-text-secondary)' }}>Status</dt>
            <dd><StatusPill status={dealer.status} /></dd>
          </div>
          <div className="flex justify-between">
            <dt style={{ color: 'var(--color-text-secondary)' }}>Created</dt>
            <dd>{pending ? '…' : (fmtDate(sum.created) || '—')}</dd>
          </div>
          <div className="flex justify-between">
            <dt style={{ color: 'var(--color-text-secondary)' }}>Last Login</dt>
            <dd>{pending ? '…' : (fmtDate(sum.lastLogin) || 'Never')}</dd>
          </div>
        </dl>
      </div>

      <div className="card p-4 space-y-3" style={{ borderColor: 'var(--color-border)' }}>
        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Seat Usage</h3>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt style={{ color: 'var(--color-text-secondary)' }}>Paid Seats</dt>
            <dd className="font-medium">{sum.paidInUse} / {paidTotal}</dd>
          </div>
          {editing ? (
            <div className="space-y-2">
              <label className="label" htmlFor="idi226-paid-seats">Paid seats</label>
              <input
                id="idi226-paid-seats"
                type="number"
                min="1"
                step="1"
                className="input w-full"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              {seatError && (
                <p className="text-sm" style={{ color: 'var(--color-status-error-text)' }}>{seatError}</p>
              )}
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="secondary" size="sm" onClick={() => setEditing(false)} disabled={savingSeats}>Cancel</Button>
                <Button type="button" size="sm" onClick={savePaidSeats} disabled={savingSeats}>Save</Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button type="button" variant="secondary" size="sm" onClick={startEdit}>Edit paid seats</Button>
            </div>
          )}
          <div className="flex justify-between">
            <dt style={{ color: 'var(--color-text-secondary)' }}>Viewer Seats</dt>
            <dd className="font-medium">{sum.viewers} (unlimited)</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

// ─── People Tab ──────────────────────────────────────────────────────────────

// IDI-479: click-to-sort for the People tab.
const DEALER_USER_SORT_COLUMNS = [
  { key: 'name', type: 'text', value: (u) => u.name || null },
  { key: 'email', type: 'text', value: (u) => u.email || null },
  { key: 'role', type: 'text', value: (u) => u.role || null },
  { key: 'seatType', type: 'text', value: (u) => seatLabel(u) },
  { key: 'status', type: 'text', value: (u) => humanizeEnum(u.status) || null },
  { key: 'lastLogin', type: 'text', value: (u) => lastLoginOf(u) },
];

function PeopleTab({ users: loaded, onUserAdded, onPasswordIssued, onSeatChanged }) {
  const users = loaded || [];
  const { sortedRows: sortedUsers, getSortProps } = useTableSort(users, DEALER_USER_SORT_COLUMNS);
  const [addOpen, setAddOpen] = useState(false);
  // IDI-623: the login a temp password is being issued to (null = closed).
  const [issueFor, setIssueFor] = useState(null);

  // IDI-252: handleSendPasswordReset removed — button disabled at render.
  // Restore handler when backend password-reset endpoint exists.

  // IDI-226: the seat pill switches an enabled Salesperson Paid↔Viewer for
  // real (IDI-277 had made it an inert <span> while there was no wire).
  const [seatError, setSeatError] = useState(null);
  const [switching, setSwitching] = useState(null);
  const handleToggleSeatType = async (user) => {
    const next = seatLabel(user) === 'Paid' ? 'viewer' : 'paid';
    setSeatError(null);
    setSwitching(user.id);
    try {
      await superAdminAPI.updateDealerUserSeat(user.id, next);
      if (onSeatChanged) onSeatChanged(user.id, next);
    } catch (err) {
      setSeatError(isNoSeats(err) ? RAISE_PAID_SEATS_MSG : "Couldn't change the seat. Try again.");
    } finally {
      setSwitching(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* IDI-252 (S1): super-admin creates a login on this dealer. */}
      <div className="flex justify-end">
        <Button onClick={() => setAddOpen(true)}>Add User</Button>
      </div>
      {seatError && (
        <p className="text-sm" style={{ color: 'var(--color-status-error-text)' }}>{seatError}</p>
      )}
      <AddDealerUserDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(u) => onUserAdded && onUserAdded(u)}
      />
      <IssueTempPasswordDialog
        user={issueFor}
        onClose={() => setIssueFor(null)}
        onIssued={() => onPasswordIssued && onPasswordIssued()}
      />
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader {...getSortProps('name')}>Name</TableHeader>
            <TableHeader {...getSortProps('email')}>Email</TableHeader>
            <TableHeader {...getSortProps('role')}>Role</TableHeader>
            <TableHeader {...getSortProps('seatType')}>Seat Type</TableHeader>
            <TableHeader {...getSortProps('status')}>Status</TableHeader>
            <TableHeader {...getSortProps('lastLogin')}>Last Login</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-6">
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{loaded === null ? 'Loading…' : 'No users found.'}</p>
              </TableCell>
            </TableRow>
          ) : (
            sortedUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>
                  {/* IDI-226: a button only where the switch is allowed (D7);
                      Admin, Field Tech and deactivated rows keep a plain pill. */}
                  {(() => {
                    const pillStyle = {
                      borderColor: seatLabel(user) === 'Paid' ? 'var(--green-200)' : 'var(--neutral-200)',
                      backgroundColor: seatLabel(user) === 'Paid' ? 'var(--green-50)' : 'var(--neutral-50)',
                      color: seatLabel(user) === 'Paid' ? 'var(--green-700)' : 'var(--neutral-700)',
                    };
                    const cls = 'text-xs px-2 py-0.5 rounded-full border inline-block';
                    return canSwitchSeat(user) ? (
                      <button
                        type="button"
                        className={`${cls} cursor-pointer`}
                        style={pillStyle}
                        disabled={switching === user.id}
                        aria-label={`Switch ${user.name || user.email} to ${seatLabel(user) === 'Paid' ? 'Viewer' : 'Paid'}`}
                        onClick={() => handleToggleSeatType(user)}
                      >
                        {seatLabel(user)}
                      </button>
                    ) : (
                      <span className={cls} style={pillStyle}>{seatLabel(user)}</span>
                    );
                  })()}
                </TableCell>
                <TableCell><StatusPill status={user.status} size="sm" /></TableCell>
                <TableCell>{fmtDate(lastLoginOf(user)) || 'Never'}</TableCell>
                <TableCell>
                  {/* IDI-623 (D5): Inactive rows get no action. */}
                  {isEnabled(user) && (
                    <Button type="button" variant="secondary" size="sm" onClick={() => setIssueFor(user)}>
                      Issue temp password
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

// ─── IDI-252 Add User (super-admin) ─────────────────────────────────────────

// D2: Admin is always Paid, Field Tech always Viewer; Salesperson either.
const LOCKED_SEAT = { admin: 'paid', field_tech: 'viewer' };
const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'salesperson', label: 'Salesperson' },
  { value: 'field_tech', label: 'Field Tech' },
];
const SEAT_OPTIONS = [
  { value: 'paid', label: 'Paid' },
  { value: 'viewer', label: 'Viewer' },
];
const EMPTY_ADD_FORM = { firstName: '', lastName: '', email: '', role: 'salesperson', seatType: 'paid' };

function AddDealerUserDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(() => ({ ...EMPTY_ADD_FORM }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // D3: the password lives only here and is cleared on close.
  const [created, setCreated] = useState(null);

  const lockedSeat = LOCKED_SEAT[form.role];
  const seatType = lockedSeat || form.seatType;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Every open starts clean, however the parent toggled it (no stale
  // password, error or form from a previous open).
  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_ADD_FORM });
      setError(null);
      setCreated(null);
      setSaving(false);
    }
  }, [open]);

  const close = () => {
    setForm({ ...EMPTY_ADD_FORM });
    setError(null);
    setCreated(null);
    setSaving(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // First name and Email are enforced by the inputs' `required` (no extra
    // copy, per the contract); anything that slips past is rejected by the
    // backend's 400 and shows the retry copy below.
    setSaving(true);
    setError(null);
    try {
      const res = await superAdminAPI.addDealerUser({
        name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        email: form.email.trim(),
        role: form.role,
        seat_type: seatType,
      });
      // superAdminAPI.addDealerUser resolves to the unwrapped payload
      // {user, temporaryPassword, emailStatus} (api.js strips the envelope).
      const user = res?.user || {};
      setCreated({ email: user.email || form.email.trim(), password: res?.temporaryPassword || '' });
      onCreated({
        id: user.id || user.email || form.email.trim(),
        email: user.email || form.email.trim(),
        name: user.name || `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        role: user.role || form.role,
        seatType: user.seatType || seatType,
        status: user.status || 'active',
      });
    } catch (err) {
      // apiClient rejects with the axios error (status on err.response).
      const status = err?.response?.status ?? err?.status;
      setError(status === 409
        ? 'A user with this email already exists'
        : status === 422 ? RAISE_PAID_SEATS_MSG
          : "Couldn't create the user. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (created) {
    return (
      <PasswordCardDialog
        open={open}
        title="User created"
        email={created.email}
        password={created.password}
        onDone={close}
      />
    );
  }

  return (
    <Dialog open={open} onClose={close}>
      <DialogHeader>Add User</DialogHeader>
      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div className="space-y-3">
            {error && (
              <p role="alert" className="text-sm" style={{ color: 'var(--color-status-error-text)' }}>{error}</p>
            )}
            <div>
              <label className="label" htmlFor="idi252-first">First name*</label>
              <input id="idi252-first" className="input" required value={form.firstName} onChange={set('firstName')} />
            </div>
            <div>
              <label className="label" htmlFor="idi252-last">Last name</label>
              <input id="idi252-last" className="input" value={form.lastName} onChange={set('lastName')} />
            </div>
            <div>
              <label className="label" htmlFor="idi252-email">Email*</label>
              <input id="idi252-email" type="email" className="input" required value={form.email} onChange={set('email')} />
            </div>
            <div>
              <span className="label">Role</span>
              <SelectMenu
                aria-label="Role"
                width="full"
                value={form.role}
                onChange={(v) => setForm((f) => ({ ...f, role: v }))}
                options={ROLE_OPTIONS}
              />
            </div>
            <div>
              <span className="label">Seat type</span>
              <SelectMenu
                aria-label="Seat type"
                width="full"
                value={seatType}
                disabled={!!lockedSeat}
                onChange={(v) => setForm((f) => ({ ...f, seatType: v }))}
                options={SEAT_OPTIONS}
              />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={close}>Cancel</Button>
          <Button type="submit" disabled={saving}>Create user</Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

// ─── IDI-252 password card (shared with IDI-623) ────────────────────────────

// The one-time password card: shown once, never stored past close.
function PasswordCardDialog({ open, title, email, password, onDone }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => { setCopied(false); }, [password]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      // Clipboard unavailable (e.g. not a secure context): select the
      // password on screen so a manual copy is one keystroke (no new copy).
      setCopied(false);
      try {
        const node = document.querySelector('[data-testid="created-password"]');
        const sel = window.getSelection && window.getSelection();
        if (node && sel && document.createRange) {
          const range = document.createRange();
          range.selectNodeContents(node);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      } catch { /* nothing else to do */ }
    }
  };

  return (
    <Dialog open={open} onClose={onDone}>
      <DialogHeader>{title}</DialogHeader>
      <DialogBody>
        <div className="space-y-3 text-sm">
          <p data-testid="created-email">{email}</p>
          <div className="flex items-center gap-2">
            <code data-testid="created-password" className="flex-1 px-3 py-2 bg-gray-100 rounded font-mono select-all">
              {password}
            </code>
            <Button type="button" variant="secondary" size="sm" onClick={handleCopy}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Shown once. Give it to them yourself; they set their own password at first sign-in.
          </p>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button type="button" onClick={onDone}>Done</Button>
      </DialogFooter>
    </Dialog>
  );
}

// ─── IDI-623 Issue temp password (super-admin) ──────────────────────────────

// Confirm, then IDI-252's card. D3: the password lives only here and is
// cleared on close; it is never emailed.
function IssueTempPasswordDialog({ user, onClose, onIssued }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [issued, setIssued] = useState(null);
  const userId = user ? (user.id || user.email) : null;

  useEffect(() => {
    setSaving(false);
    setError(null);
    setIssued(null);
  }, [userId]);

  const close = () => {
    setSaving(false);
    setError(null);
    setIssued(null);
    onClose();
  };

  const handleIssue = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await superAdminAPI.issueTempPassword(userId);
      setIssued({ email: res?.email || user.email, password: res?.temporaryPassword || '' });
      onIssued();
    } catch {
      setError("Couldn't issue a password. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (issued) {
    return (
      <PasswordCardDialog
        open={!!user}
        title="Temporary password issued"
        email={issued.email}
        password={issued.password}
        onDone={close}
      />
    );
  }

  return (
    <Dialog open={!!user} onClose={close}>
      <DialogHeader>Issue temp password</DialogHeader>
      <DialogBody>
        <div className="space-y-3 text-sm">
          {error && (
            <p role="alert" style={{ color: 'var(--color-status-error-text)' }}>{error}</p>
          )}
          <p>
            Issue a temporary password for {user?.email}? Their current password stops working.
          </p>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="secondary" onClick={close}>Cancel</Button>
        <Button type="button" onClick={handleIssue} disabled={saving}>Issue temp password</Button>
      </DialogFooter>
    </Dialog>
  );
}

// ─── Reports Tab ─────────────────────────────────────────────────────────────

function ReportsTab({ dealer }) {
  return (
    <div className="card p-4" style={{ borderColor: 'var(--color-border)' }}>
      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
        Contractor-specific reports will be available here.
      </p>
    </div>
  );
}

// ─── Audit Log Tab (IDI-621) ─────────────────────────────────────────────────
// Every super-admin change to this dealer, newest first. Selecting a row shows
// the full Before and After.

function auditJSON(v) {
  if (v === null || v === undefined) return '—';
  return JSON.stringify(v, null, 2);
}

function AuditLogTab({ dealerId }) {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);
  // IDI-661: read 50 at a time; "Load more" appends the next older page.
  const [page, setPage] = useState({ hasMore: false, nextCursor: null });
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(false);
  const asPage = (res) => (Array.isArray(res) ? { list: res, hasMore: false, nextCursor: null } : res);

  useEffect(() => {
    let cancelled = false;
    superAdminAPI
      .getAuditLog(dealerId)
      .then((res) => {
        if (cancelled) return;
        const p = asPage(res);
        setEntries(p.list);
        setPage({ hasMore: !!p.hasMore, nextCursor: p.nextCursor || null });
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [dealerId]);

  const loadMore = () => {
    setLoadingMore(true);
    setMoreError(false);
    superAdminAPI
      .getAuditLog(dealerId, page.nextCursor)
      .then((res) => {
        const p = asPage(res);
        setEntries((prev) => [...prev, ...p.list]);
        setPage({ hasMore: !!p.hasMore, nextCursor: p.nextCursor || null });
      })
      .catch(() => setMoreError(true))
      .finally(() => setLoadingMore(false));
  };

  if (error) {
    return <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Couldn't load the audit log.</p>;
  }
  if (entries === null) return <SkeletonCard />;

  const current = selected === null ? null : entries[selected];
  return (
    <div className="space-y-4">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>When</TableHeader>
            <TableHeader>Who</TableHeader>
            <TableHeader>Setting</TableHeader>
            <TableHeader>Change</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center py-6">
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>No changes recorded yet.</p>
              </TableCell>
            </TableRow>
          ) : (
            entries.map((e, i) => (
              <TableRow key={`${e.at}-${i}`} onClick={() => setSelected(i)} aria-selected={selected === i}>
                <TableCell>{e.at ? new Date(e.at).toLocaleString() : ''}</TableCell>
                <TableCell>{e.who?.email}</TableCell>
                <TableCell>{e.setting}</TableCell>
                <TableCell>{e.change}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {moreError && (
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Couldn't load the audit log.</p>
      )}
      {page.hasMore && (
        <Button variant="secondary" onClick={loadMore} disabled={loadingMore}>Load more</Button>
      )}
      {current && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[['Before', current.before], ['After', current.after]].map(([label, value]) => (
            <div key={label} className="card p-4" style={{ borderColor: 'var(--color-border)' }}>
              <h3 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>{label}</h3>
              <pre className="text-xs whitespace-pre-wrap break-all" style={{ color: 'var(--color-text-secondary)' }}>
                {auditJSON(value)}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
